import React, { useState, useContext, useEffect } from "react";
import Helmet from "react-helmet";
import styled from "@emotion/styled";
import { Link } from "gatsby";
import { Box } from "@src/components/Boxes";
import { mediaQueries, mediaQueriesMax } from "@src/styles/theme";
import { logEvent } from "@src/utils/logClient";
import { AppDataContext } from "@src/components/AppDataContext";
import { Title, Button, theme, Space, Heading } from "@src/components/nessie-web";
import { PlayIcon, FileIcon } from "@classdojo/web/nessie/icons";
import IdeaPreviewCard from "@src/components/ideas/IdeaPreviewCard";
import T from "@src/components/translation/Translate";
import { getRelativePath } from "@src/utils/routes";
import NotTranslatedBanner from "@src/components/ideas/NotTranslatedBanner";

const tags = ["CCSS.ELA-LITERACY.SL.4.1", "Growth Mindset"];

const LessonBack = styled("div")`
  font-size: 16px;
  font-weight: 500;
  color: #00aeef;
  line-height: 1;

  &:hover {
    cursor: pointer;
    color: #00baff;
  }
`;

const LessonContainer = styled("div")`
  max-width: 960px;
  margin: auto;
  margin-top: ${theme.space.l}px;
  padding: 0 ${theme.space.s}px;
`;

const LessonHero = styled("div")`
  height: 280px;
  background-size: cover;
  background-position: center;
  border-radius: ${theme.radii.dt_radius_m};
  ${mediaQueriesMax[0]} {
    border-radius: 0;
    margin: 0 -${theme.space.s}px;
  }
`;

const LessonContent = styled("div")`
  margin-top: ${theme.space.m}px;
  margin-bottom: 144px;
  ${mediaQueries[0]} {
    margin-top: ${theme.space.xl}px;
    display: flex;
  }
`;

const LessonCtas = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.m}px;
  margin-bottom: ${theme.space.l}px;
  ${mediaQueries[0]} {
    gap: ${theme.space.l}px;
    margin-bottom: 0;
    margin-right: ${theme.space.l}px;
    min-width: 360px;
  }
`;

const ActivityButton = styled(Button)`
  width: 100%;
`;

const DiscussionGuideButton = styled(Button)`
  span:first-letter {
    text-transform: uppercase;
  }
`;

const LessonInfo = styled("div")``;

const LessonDescription = styled("div")`
  font-size: 18px;
  line-height: 22px;
  color: ${theme.colors.taro50};
  font-weight: 600;

  p {
    margin-bottom: 20px;
  }

  ${mediaQueriesMax.lesson} {
    width: 100%;
  }
`;

const LessonRecommendations = styled("div")`
  margin-bottom: ${theme.space.xxl}px;
  ${mediaQueries[0]} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: ${theme.space.m}px;
  }
`;

const Lesson = ({ pageContext }) => {
  const appData = useContext(AppDataContext);

  const [country, setCountry] = useState(null);

  const { lesson, board } = pageContext;
  const { us_only } = lesson;
  const backLinkEnabled = false;

  const meta = {
    title: lesson.title,
    teaser: lesson.description,
  };

  let backLink = null;

  if (board) {
    backLink = (
      <LessonBack>
        <Link to={board.slug}>&larr; {board.title}</Link>
      </LessonBack>
    );
  }

  useEffect(() => {
    fetch("/api/country", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => r.json())
      .then((json) => setCountry(json.country));

    logEvent({
      eventName: "ideas.activityView",
      eventValue: lesson.slug,
    });
  }, []);

  const logActivityStart = () =>
    logEvent({
      eventName: "ideas.activityStart",
      eventValue: lesson.slug,
    });

  return (
    <div>
      <Helmet>
        <title>{meta.title} - Big Ideas - Classdojo</title>
        <script src="https://cdn.radiantmediatechs.com/rmp/7.11.3/js/rmp.min.js" key="rmp" />
      </Helmet>
      <Box backgroundColor="white">
        <LessonContainer>
          {pageContext.localeStrings.currentLocale !== "en-US" && !lesson.isTranslated && <NotTranslatedBanner />}
          <Title textAlign="center">{lesson.title}</Title>
          <Space size="l" />
          <LessonHero style={{ backgroundImage: `url(${lesson.header_poster_url.file.publicURL})` }} />
          <LessonContent>
            <LessonCtas>
              <Link onClick={logActivityStart} to={getRelativePath(`/f/${lesson.slug}/0`)}>
                <ActivityButton kind={"primary"} icon={<PlayIcon />}>
                  <T path="ideas.start_activity" />
                </ActivityButton>
              </Link>
              {lesson.discussion_guide_link && (
                <DiscussionGuideButton href={lesson.discussion_guide_link} kind={"secondary"} icon={<FileIcon />}>
                  {lesson.discussion_guide_label ? lesson.discussion_guide_label : <T path="ideas.discussion_guide" />}
                </DiscussionGuideButton>
              )}
            </LessonCtas>
            <LessonInfo>
              <LessonDescription
                dangerouslySetInnerHTML={{
                  __html: lesson.description,
                }}
              />
            </LessonInfo>
          </LessonContent>
          {lesson.related.length > 0 && (
            <>
              <Heading>
                <T path="ideas.up_next" />
              </Heading>
              <Space size="m" />
              <LessonRecommendations>
                {lesson.related.slice(0, 3).map((relatedLesson) => {
                  return (
                    <IdeaPreviewCard
                      key={relatedLesson.ideas_lesson_right.slug}
                      idea={relatedLesson.ideas_lesson_right}
                      preview
                    />
                  );
                })}
              </LessonRecommendations>
            </>
          )}
        </LessonContainer>
      </Box>
    </div>
  );
};

export default Lesson;
